/**
 * Mirrors `VpcPeeringEligibility.ELIGIBLE` in hermes
 * (hermes-engine/src/main/java/io/hevo/hermes/connectivity/VpcPeeringEligibility.java).
 *
 * An SSH tunnel over VPC peering only routes if the database sits in the
 * customer's peered AWS VPC, so hermes gates on the entity type as well as on
 * the template carrying an SSH component. Edge needs no copy of this list — the
 * `vpc_peering` key is simply absent from an ineligible template. Standard has
 * no template to read, so it keeps this list to avoid issuing a request that
 * can only ever answer NONE.
 *
 * The self-hosted generic types are included deliberately: a database on EC2
 * inside the peered VPC is the strongest peering case of all. AURORA carries no
 * AWS_ prefix, so a prefix match would silently miss it — hence an explicit set.
 *
 * DUPLICATED BY DESIGN, AND THEREFORE FRAGILE: if hermes adds a type, this list
 * must be updated in the same release or Standard will hide a toggle that Edge
 * shows. The `*_VIA_MYSQL` app variants (PrestaShop, Magento, WooCommerce,
 * WordPress), ORACLE_MYSQL, AMAZON_RDS, TOKUDB and TOKU_DB_MYSQL are still
 * unclassified by backend and so are withheld — they render an SSH block and
 * keep manual SSH only.
 *
 * MONGODB is withheld too, and that one is a DELIBERATE DIVERGENCE the other
 * way: hermes lists it as eligible, so Edge offers peering there while Standard
 * does not. Do not "fix" it by re-syncing to hermes — the pinning test below
 * records the intent. Keep `vpcPeering.test.ts` in step: it pins the exact
 * set.
 */
export const VPC_PEERING_ELIGIBLE_ENTITY_TYPES = [
  // Amazon-managed
  'AURORA',
  'AWS_AURORA_POSTGRES',
  'AWS_ELASTICSEARCH',
  'AWS_OPENSEARCH',
  'AWS_RDS_MARIA_DB',
  'AWS_RDS_MSSQL',
  'AWS_RDS_MYSQL',
  'AWS_RDS_ORACLE',
  'AWS_RDS_POSTGRES',
  'AWS_RDS_SQLSERVER',
  'REDSHIFT',
  // Self-hosted / generic
  // ELASTICSEARCH is app-side only, and the one deliberate divergence from
  // hermes: its set names AWS_ELASTICSEARCH and AWS_OPENSEARCH, and the form
  // renders no SSH block for either, while the self-hosted variant that does
  // render one is absent. Without this entry peering can reach no
  // Elasticsearch at all.
  'ELASTICSEARCH',
  'MARIA_DB',
  'MS_SQL',
  'MYSQL',
  'ORACLE',
  'POSTGRES',
  'SQLSERVER'
] as const;

const ELIGIBLE = new Set<string>(VPC_PEERING_ELIGIBLE_ENTITY_TYPES);

/** Case-insensitive and whitespace-trimmed, matching hermes's own comparison. */
export function isVpcPeeringEligible(entityType: unknown): boolean {
  return (
    typeof entityType === 'string' && ELIGIBLE.has(entityType.trim().toUpperCase())
  );
}

/**
 * The tunnel port. Standard has no served spec any more — hermes's
 * `GET /config/v2.0/connectivity/vpc-peering` is gone — so the only thing the
 * team settings flag carries is a host, and the port comes back to the client
 * as a constant. This reverses LLD R16 for Standard only; Edge still reads
 * `port` off the `vpc_peering` key on its connector template, so nothing about
 * the Edge path hardcodes it.
 *
 * The peering SSH user is NOT declared here: the toolkit exports
 * `VPC_PEERING_SSH_USER`, and `isPeeringShaped` compares against that same
 * value — a second copy is what would let the two disagree.
 */
export const VPC_PEERING_SSH_PORT = 1278;
